import { useRouter } from '@abyss/web/hooks/useRouter';
import { storage } from '@abyss/web/tools/storage';
import { Badge } from '@abyss/web/ui/Badge';
import { IconMaterial } from '@abyss/web/ui/IconMaterial';
import { IconSymbol } from '@abyss/web/ui/IconSymbol';
import { find } from 'lodash';
import { useTranslation } from 'react-i18next';

import { useCurrentMember } from '../../hooks/useCurrentMember';
import { useLagoon } from '../../hooks/useLagoon';
import { ProviderType } from '../../models/ProviderDetails';
import { isPcpSuppressionEligible } from '../../utils/pcpSuppression.utils';
import { getLagoonConfigValue } from '../../utils/providerSearch.utils';
import { loginNotRequired } from '../../utils/session.utils';
import { adobeLinkTrackEvent } from '../AdobeTagging/adobeLinkTrackEvent';
import {
  CareCategories,
  Constants,
  CoverageTypesCodes,
  ProviderTypes,
  ReverseCoverageTypesCodes,
} from '../Constants';
import { ConstantsLagoon } from '../ConstantsLagoon';
import { ConstantsRoutes } from '../ConstantsRoutes';
import { convertObjectToUrlParams, getFeatureFlag } from '../Utils';
import { formatDateWithoutLeadingZeros } from '../Utils/datesUtils/formatDate';
import { checkTerminationDate } from '../Utils/terminateDateIndicatorUtils';
import {
  CareTeamCardHeader,
  CareTeamCardLink,
  CareTeamCardTitle,
} from './CareTeamCard.styled';
import { OutOfNetworkLabelCss, PcpBadge } from './CareTeamUtils.styled';

const OutOfNetworkLabel = ({ providerName, msg }) => (
  <OutOfNetworkLabelCss
    data-testid={`out-of-network-label-${providerName
      ?.replace(/\s+/g, '-')
      .toLowerCase()}`}
  >
    {msg}
  </OutOfNetworkLabelCss>
);

export const PcpNetworkStatusBadge = ({
  isPcp,
  isOutOfDirectory,
  suppressed,
  oon,
  goingOON,
  isFuturePcp,
  showEffectiveEndDate,
  showEffectiveStartDate,
  pcpInfo,
  isStaffModel = false,
  isStaffModelPCPInNetwork,
}) => {
  const { t } = useTranslation();
  const featureFlags = useLagoon(Constants.LAGOON_TABLE.FEATURE_FLAGS)();
  const currentMember = useCurrentMember();

  const enableNonGatedPcpSuppression = getFeatureFlag(
    featureFlags,
    ConstantsLagoon.FEATURE_FLAGS.ENABLE_NON_GATED_PCP_SUPPRESSION
  );

  const isPcpSuppressed = isPcpSuppressionEligible(
    currentMember,
    enableNonGatedPcpSuppression
  );

  const shouldShowBadge = isPcp || isFuturePcp;
  if (!shouldShowBadge || isPcpSuppressed) return null;

  const badgeVariant = isFuturePcp ? 'info' : 'success';

  let currentPcpLabel = t('PCP');
  let futurePcpLabel = t('PRIMARY_CARE_PROVIDER.FUTURE_PCP');

  const endDate = pcpInfo?.endDate;
  const startDate = pcpInfo?.futurePCP?.startDate;

  const formatEndDate = endDate
    ? formatDateWithoutLeadingZeros(endDate, true)
    : '';
  const formatStartDate = startDate
    ? formatDateWithoutLeadingZeros(startDate, true)
    : '';

  if (showEffectiveEndDate && endDate) {
    currentPcpLabel = `${t('PCP')} • ${t('PRIMARY_CARE_PROVIDER.UNTIL', {
      endDate: formatEndDate,
    })}`;
  }

  if (showEffectiveStartDate && startDate) {
    futurePcpLabel = `${t('PRIMARY_CARE_PROVIDER.FUTURE_PCP')} • ${t(
      'PRIMARY_CARE_PROVIDER.STARTING_DATE',
      { startDate: formatStartDate }
    )}`;
  }

  const pcpLabel = isFuturePcp ? futurePcpLabel : currentPcpLabel;

  return (
    <>
      <Badge
        className={PcpBadge.className}
        data-testid={isFuturePcp ? 'future-pcp-badge' : 'pcp-badge'}
        outline
        variant={badgeVariant}
      >
        {pcpLabel}
      </Badge>
      {suppressed ? null : goingOON ? (
        <Badge
          data-testid={
            isFuturePcp
              ? 'future-going-out-of-network-badge'
              : 'going-out-of-network-badge'
          }
          outline
          style={{
            margin: '4px 0px',
          }}
          variant="warning"
        >
          <IconSymbol
            color="$warning1"
            css={{
              verticalAlign: 'middle',
            }}
            icon="warning"
            size="14"
          />{' '}
          {t('Going out-of-network')}
        </Badge>
      ) : (
          isStaffModel
            ? isStaffModelPCPInNetwork !== undefined &&
              !isStaffModelPCPInNetwork
            : oon || isOutOfDirectory
        ) ? (
        <Badge
          data-testid={
            isFuturePcp ? 'future-out-of-network-badge' : 'out-of-network-badge'
          }
          outline
          style={{
            margin: '4px 0px',
          }}
          variant="warning"
        >
          <IconSymbol
            color="$warning1"
            css={{
              verticalAlign: 'middle',
            }}
            icon="warning"
            size="14"
          />{' '}
          {t('Out of network')}
        </Badge>
      ) : null}
    </>
  );
};

export const OutOfNetworkBadge = ({
  msg,
  providerName,
  type = 'badge',
  isGoingOutOfNetwork = false,
}) => {
  if (
    isGoingOutOfNetwork ||
    msg === 'Out-of-network' ||
    msg === 'Out of network'
  ) {
    return (
      <Badge
        data-testid={`out-of-network-badge-${providerName
          ?.replace(/\s+/g, '-')
          .toLowerCase()}`}
        key={msg}
        outline
        style={{
          margin: '4px 0px',
        }}
        variant="warning"
      >
        <IconMaterial
          color="$warning1"
          css={{
            verticalAlign: 'middle',
          }}
          icon="warning"
          size="14"
        />{' '}
        {msg}
      </Badge>
    );
  }

  if (type === 'badge') {
    return (
      <Badge
        data-testid={`out-of-network-badge-${providerName
          ?.replace(/\s+/g, '-')
          .toLowerCase()}`}
        outline
        style={{ margin: '4px 0px', fontWeight: 600 }}
        variant="neutral"
      >
        {msg}
      </Badge>
    );
  }

  return <OutOfNetworkLabel msg={msg} providerName={providerName} />;
};

export const getSpeciality = (provider) => {
  if (!provider?.specialities || provider?.specialities.length === 0) {
    return '';
  }
  const isSpeciality = provider?.specialities
    ?.map((speciality) => speciality)
    .join(', ');

  return isSpeciality;
};
export const resolveLinkForDetailPage = (provider, chipStore) => {
  const { setChipValue, setOriginalChipValue, setCoverageType } =
    chipStore || {};
  const providerId = provider?.providerId || '';
  const showDirection = provider?.showDirection || false;
  const locationId =
    Array.isArray(provider?.providerLocations) &&
    provider?.providerLocations.length > 0
      ? provider?.providerLocations[0]?.locationId
      : '';
  const isTiered = provider?.providerLocations?.isTieredProvider || false;
  const chipValue =
    provider?.originalChipValue ||
    provider?.chipValue ||
    CareCategories.PRIMARY_CARE;

  setChipValue(chipValue);
  setOriginalChipValue(chipValue);
  const coverageType =
    ReverseCoverageTypesCodes[
      find(Constants.MOCK_CARE_CATEGORIES, {
        value: chipValue,
      })?.coverageType || CoverageTypesCodes.MEDICAL
    ];
  setCoverageType(coverageType);
  const searchRadius = provider?.searchRadius || 5;
  const urlParams = convertObjectToUrlParams(chipStore, {
    coverageType,
    providerId,
    showDirection,
    locationId,
    isTieredSnackCard: isTiered,
    searchRadius,
    chipValue,
  });

  let path;
  if (provider.providerType === ProviderType.PRACTITIONER) {
    path = `${ConstantsRoutes.PROVIDER_DETAILS.path}${urlParams}`;
  } else if (provider.providerType === ProviderType.MEDICAL_GROUP) {
    path = `${ConstantsRoutes.PROVIDER_GROUP_DETAILS.path}${urlParams}`;
  } else path = `${ConstantsRoutes.FACILITY_DETAILS.path}${urlParams}`;

  return path;
};

export const providerName = (provider) => {
  const providerName = provider?.providerName || '';
  const firstName = provider?.firstName || '';
  const middleName = provider?.middleName || '';
  const lastName = provider?.lastName || '';
  return providerName !== ''
    ? providerName
    : `${firstName} ${middleName} ${lastName}`;
};

export const isInvalidCurrentPcpID = (pcpInfo: any) =>
  !!pcpInfo?.hasPCP &&
  !!pcpInfo?.pcpID?.toString()?.trim() &&
  !pcpInfo?.providerID?.toString()?.trim() &&
  !pcpInfo?.pcpName?.toString()?.trim();

export const isInvalidFuturePcpID = (pcpInfo: any) =>
  pcpInfo?.futurePCP != null &&
  !!pcpInfo?.futurePCP?.pcpID?.toString()?.trim() &&
  !pcpInfo?.futurePCP?.providerID?.toString()?.trim() &&
  !pcpInfo?.futurePCP?.pcpName?.toString()?.trim();

export const CareTeamCardHeaderSection = (_css?) => {
  const { t } = useTranslation();
  const { navigate } = useRouter();

  return (
    <CareTeamCardHeader css={_css.css}>
      <CareTeamCardTitle>
        {' '}
        {t('CARE_TEAM.CARE_TEAM_BREADCRUMB')}
      </CareTeamCardTitle>
      <CareTeamCardLink
        aria-label="View all & edit Care team"
        css={{ width: 'fit-content' }}
        data-auto-testid="view-all-providers-link"
        data-testid="view-all-providers-link-care-team"
        href="#"
        onClick={(e) => {
          e.preventDefault();
          navigate(`${ConstantsRoutes.CARE_TEAM.path}`);
          adobeLinkTrackEvent({
            name: ANALYTICS_CONSTANTS.VIEW_ALL,
            location: ANALYTICS_CONSTANTS.CARE_TEAM_SNACK_CARD,
            type: ANALYTICS_CONSTANTS.INTERNAL_TYPE,
          });
        }}
      >
        {t('View all')} & {t('Edit')}
      </CareTeamCardLink>
    </CareTeamCardHeader>
  );
};

export const filterCareTeamChipsByCoverageType = (
  careTeamChips,
  memberInfo
) => {
  if (!careTeamChips || careTeamChips.length === 0) {
    return [];
  }
  Constants.COVERAGE_TYPECODE_TO_CHIP.B = CoverageTypesCodes.BEHAVIORAL_HEALTH;
  const memberCoverage: string[] = memberInfo?.eligibility
    .flatMap((coverage) => coverage?.memberHealthCoverage?.coverageType)
    .map(
      (coverageType) =>
        Constants.COVERAGE_TYPECODE_TO_CHIP[
          coverageType?.typeCode?.coverageTypeCode || ''
        ]
    );
  const result = Constants.MOCK_CARE_CATEGORIES.filter(
    (category) =>
      memberCoverage.includes(category.coverageType) &&
      careTeamChips.find((chip) => CareCategories[chip] === category.value)
        ?.length > 0
  ).map((category) =>
    careTeamChips.find((chip) => CareCategories[chip] === category.value)
  );
  return result;
};

export const ANALYTICS_CONSTANTS = {
  BOOKMARK_ICON: 'bookmark icon',
  PROVIDER_SNACK_CARD: 'provider snack card',
  VIEW_ALL: 'view all & edit',
  INTERNAL_TYPE: 'internal',
  CARE_TEAM_SNACK_CARD: 'care team snack card',
  CHANGE_YOUR_PCP: 'change your pcp',
  EDIT_PCP_DROPDOWN: 'edit pcp dropdown',
  PCP_SNACK_CARD: 'pcp snack card',
  CHANGE_YOUR_PCP_LOCATION: 'change your pcp location',
  CARE_TEAM: 'care team',
  SORT_BY: 'body: sort by',
  SORT_RESULTS: 'sort results',
  PRIMARY_CARE_VIEW_CARE_TEAM: 'primary care view care team',
  PRIMARY_CARE: 'primary care',
};

/**
 * Determines if the current user is a superuser.
 *
 * @deprecated This function now uses the shared superUserUtils implementation.
 * Consider importing directly from '../Widget/SmartChoiceWidget/superUserUtils'
 * for new code to use the single source of truth.
 *
 * @param allConfigs - Lagoon configuration accessor function
 * @returns boolean - true if user is a superuser, false otherwise
 */
export const getIsSuperUser = (allConfigs) =>
  loginNotRequired() ? false : isUhcSuperUser(allConfigs);

/**
 * Legacy function for determining superuser status.
 *
 * @deprecated This function is kept for backward compatibility but now delegates
 * to the shared superUserUtils implementation. Use determineIsSuperUser from
 * superUserUtils directly for new code.
 *
 * @param allConfigs - Lagoon configuration accessor function
 * @returns boolean | null - true if user is a superuser, false or null otherwise
 */
export const isUhcSuperUser = (allConfigs) => {
  const hostname = window?.location?.hostname?.toLowerCase() ?? '';
  const isSuperUserFromSession = storage.session.get(
    Constants.STORAGE_KEYS.SESSION.SUPER_USER
  );
  if (isSuperUserFromSession !== null && isSuperUserFromSession !== undefined) {
    return isSuperUserFromSession;
  }
  const includedHostNamesForSuperUser = getLagoonConfigValue(
    allConfigs,
    'INCLUDED_HOST_NAMES_FOR_SUPER_USER'
  );
  if (
    !includedHostNamesForSuperUser
      .split(',')
      .filter((s) => s && s.trim() !== '')
      .some((host) => hostname.includes(host))
  ) {
    return false;
  }
  return null;
};

export const checkMedicalGroupProviderType = (providerType) =>
  providerType?.toLowerCase() === ProviderTypes.MEDICAL_GROUP.toLowerCase();

export const getIsKelseySeyboldPlanMember = (
  isKelseySeyboldPlan,
  enableNewPcpCareTeamFlow,
  showFuturePcpCard,
  showCurrentPcpCard,
  pcpInfo
) => {
  if (!isKelseySeyboldPlan) return false;

  if (enableNewPcpCareTeamFlow) {
    if (showFuturePcpCard) {
      return checkMedicalGroupProviderType(pcpInfo?.futurePCP?.providerType);
    }
    if (showCurrentPcpCard) {
      return checkMedicalGroupProviderType(pcpInfo?.providerType);
    }
    return false;
  }
  return checkMedicalGroupProviderType(pcpInfo?.providerType);
};

export const getIsSanitasPlanMember = (
  isSanitasPlan,
  enableNewPcpCareTeamFlow,
  showFuturePcpCard,
  showCurrentPcpCard,
  pcpInfo,
  sanitasPCP
) => {
  if (!isSanitasPlan) return false;

  if (enableNewPcpCareTeamFlow) {
    if (showFuturePcpCard) {
      return pcpInfo?.futurePCP?.pcpID === sanitasPCP;
    }
    if (showCurrentPcpCard) {
      return pcpInfo?.pcpID === sanitasPCP;
    }
    return false;
  }
  return pcpInfo?.pcpID === sanitasPCP;
};

export const getIsPcpStaffModel = (
  isStaffModelBookmarkEnabled: boolean,
  isPcp: boolean,
  isFuturePcp: boolean,
  pcpInfo: any
): boolean =>
  isStaffModelBookmarkEnabled &&
  ((isPcp && !!pcpInfo?.isStaffModel) ||
    (isFuturePcp && !!pcpInfo?.futurePCP?.isStaffModel));

/**
 * For staff model PCP/future PCP, find the first INN location across all
 * provider locations to determine going-OON, instead of just using the first location.
 */
export const getStaffModelGoingOON = (
  providerLocations: any[] | undefined,
  displayTerminationDateEnabled: boolean
): boolean => {
  const innLocation = providerLocations?.find(
    (loc) => loc.network?.status === 'INN'
  );
  if (!innLocation) return false;
  return checkTerminationDate(
    displayTerminationDateEnabled,
    innLocation.network?.end
  );
};

/**
 * Determines if PCP should be hidden based on member's viewPcp setting and group membership.
 * Returns true if member cannot view PCP or is a group MNR member.
 */
export const shouldHidePcpCheck = (
  viewPcp: any,
  isGroupMNRMember: boolean
): boolean => !viewPcp || isGroupMNRMember;

/**
 * Determines if the PCP card should be hidden based on various conditions.
 * For non-gated PCP suppression, always returns false (PCP should be shown in care team list).
 */
export const shouldHidePcpCard = (
  isPcpSuppressed: boolean,
  nonGatedPcpId: string | null | undefined,
  enableNewPcpCareTeamFlow: boolean,
  hidePcpCheck: boolean,
  hasPCPFetchError: boolean,
  showCurrentPcpCard: boolean,
  showFuturePcpCard: boolean,
  isCurrentPcpCardVisible: boolean,
  isFuturePcpCardVisible: boolean,
  hasFetchedNonGatedPrimaryCareData: boolean
): boolean => {
  // For non-gated PCP suppression: PCP shows in care team list, not as separate card
  if (isPcpSuppressed && (!hasFetchedNonGatedPrimaryCareData || nonGatedPcpId)){
    return false;
  }

  if (enableNewPcpCareTeamFlow) {
    return (
      hidePcpCheck ||
      hasPCPFetchError ||
      (!showCurrentPcpCard && !showFuturePcpCard) ||
      (!isCurrentPcpCardVisible && !isFuturePcpCardVisible)
    );
  }

  return hidePcpCheck;
};

export const canFetchCareTeam = (
  primaryCare?: unknown,
  hidePCPCard?: boolean,
  isPcpSuppressed?: boolean,
  hasFetchedNonGatedPrimaryCareData?: boolean,
  isSuperUser?: boolean | null,
  isSmartChoiceEnabled?: boolean,
  preferenceData?: unknown
): boolean => {
  // Check 1: Primary care ready
  const primaryCareOk =
    primaryCare !== undefined ||
    !!hidePCPCard ||
    (isPcpSuppressed && hasFetchedNonGatedPrimaryCareData);

  // Check 2: Super user status determined
  const superUserOk = isSuperUser != null;

  // Check 3: SmartChoice ready (if enabled)
  const smartChoiceOk = !isSmartChoiceEnabled || preferenceData !== undefined;

  return primaryCareOk && superUserOk && smartChoiceOk;
};
